import { Controller } from '@hotwired/stimulus';

// Drives Ui::DocumentUploaderComponent. Handles drag-and-drop, file
// validation, and the "selected file" preview before form submission.
//
// On a valid file pick, dispatches `document-uploader-component:file-selected`
// with `{ file }` so callers can wire async upload flows if needed; while such
// an upload runs they can show the loading state via the `loading` value.

const DROPZONE = {
  filled: 'ui-document-uploader__dropzone--filled',
  error: 'ui-document-uploader__dropzone--error',
  dragging: 'ui-document-uploader__dropzone--dragging'
};

const HINT = {
  error: 'ui-hint--error'
};

const FILE_INFO = {
  hasFile: 'ui-document-uploader__file-info--has-file',
  selector: '.ui-document-uploader__file-info'
};

export default class extends Controller {
  static targets = ['dropzone', 'dropOverlay', 'input', 'message', 'radio', 'previewButton', 'loading'];

  static values = {
    accept: String,
    maxSize: Number,
    deleteUrl: String,
    title: { type: String, default: 'Click to upload' },
    subtitle: String,
    loading: Boolean
  };

  // ── Lifecycle ────────────────────────────────────────────────────────────
  connect() {
    this.dragCounter = 0;
    this.boundRadioGroupChange = this.handleRadioGroupChange.bind(this);
    document.addEventListener('change', this.boundRadioGroupChange);
    this.syncInputName();
  }

  disconnect() {
    document.removeEventListener('change', this.boundRadioGroupChange);
  }

  // Callers flip `data-document-uploader-component-loading-value` while an
  // async upload is in flight; the server renders the same two blocks.
  loadingValueChanged(loading) {
    this.element.setAttribute('aria-busy', String(loading));
    if (this.hasLoadingTarget) this.loadingTarget.hidden = !loading;
    if (this.hasDropzoneTarget) this.dropzoneTarget.hidden = loading;
  }

  // ── Public actions (wired from data-action) ──────────────────────────────
  open(event) {
    if (this.isDisabled()) return;
    if (event && event.target.closest('.ui-button, .ui-icon-button, .ui-radio-button')) return;

    if (this.isSelectable() && this.isFilled()) {
      this.selectOwnRadio();
      return;
    }
    this.inputTarget.click();
  }

  replace(event) {
    event.stopPropagation();
    if (this.isDisabled()) return;
    this.inputTarget.click();
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  keydown(event) {
    if (this.isDisabled()) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    this.open(event);
  }

  fileChanged(event) {
    const file = event.target.files && event.target.files[0];
    if (file) this.processFile(file);
  }

  dragOver(event) {
    event.preventDefault();
  }

  dragEnter(event) {
    event.preventDefault();
    if (this.isDisabled()) return;
    this.dragCounter += 1;
    this.setDragging(true);
  }

  dragLeave(event) {
    event.preventDefault();
    this.dragCounter = Math.max(0, this.dragCounter - 1);
    if (this.dragCounter === 0) this.setDragging(false);
  }

  drop(event) {
    event.preventDefault();
    this.dragCounter = 0;
    this.setDragging(false);
    if (this.isDisabled()) return;

    const file = event.dataTransfer.files && event.dataTransfer.files[0];
    if (!file) return;

    if (this.hasInputTarget && typeof DataTransfer === 'function') {
      const dt = new DataTransfer();
      dt.items.add(file);
      this.inputTarget.files = dt.files;
    }
    this.processFile(file);
  }

  // ── File processing ──────────────────────────────────────────────────────
  processFile(file) {
    this.clearError();

    if (!this.isAcceptable(file)) {
      this.restoreSelection();
      this.showError(this.invalidFileMessage());
      return;
    }

    this.selectedFile = file;
    this.renderSelectedFile(file);
    this.dispatch('file-selected', { detail: { file } });
  }

  // The browser has already swapped the rejected file into the input, and the error we show
  // promises the chosen document survives a failed replacement.
  restoreSelection() {
    if (!this.hasInputTarget) return;

    if (!this.selectedFile || typeof DataTransfer !== 'function') {
      this.inputTarget.value = '';
      return;
    }

    const transfer = new DataTransfer();
    transfer.items.add(this.selectedFile);
    this.inputTarget.files = transfer.files;
  }

  isAcceptable(file) {
    return this.isValidType(file) && file.size <= this.maxSizeValue;
  }

  invalidFileMessage() {
    return this.isFilled()
      ? 'To replace, please select a valid format and size document'
      : 'Please select a valid format and size document';
  }

  isValidType(file) {
    if (!this.acceptValue) return true;
    const mime = (file.type || '').toLowerCase();
    const filename = file.name.toLowerCase();

    return this.acceptValue.split(',').some((pattern) => {
      const type = pattern.trim().toLowerCase();
      if (!type) return false;
      if (type.startsWith('.')) return filename.endsWith(type);
      if (type.endsWith('/*')) return mime.startsWith(type.slice(0, -1));
      if (type.includes('/')) return mime === type;
      const ext = filename.split('.').pop() || '';
      return ext === type;
    });
  }

  // ── Rendering after a successful pick ────────────────────────────────────
  renderSelectedFile(file) {
    if (!this.hasDropzoneTarget) return;

    this.markFilled(file.name);
    this.replaceFileInfo(file.name, this.formatSize(file.size));

    if (this.hasPreviewButtonTarget) this.previewButtonTarget.remove();

    if (this.hasRadioTarget) {
      this.radioTarget.checked = true;
      this.syncInputName();
    }
  }

  markFilled(fileName) {
    const dz = this.dropzoneTarget;
    dz.classList.add(DROPZONE.filled);
    dz.classList.remove(DROPZONE.error);
    dz.setAttribute('aria-label', `Uploaded: ${fileName}`);
  }

  replaceFileInfo(name, size) {
    const fileInfo = this.dropzoneTarget.querySelector(FILE_INFO.selector);
    if (!fileInfo) return;
    fileInfo.classList.add(FILE_INFO.hasFile);
    fileInfo.replaceChildren(
      this.buildSpan('ui-document-uploader__title', name),
      this.buildSpan('ui-document-uploader__subtitle', size)
    );
  }

  buildSpan(className, text) {
    const el = document.createElement('span');
    el.className = className;
    el.textContent = text;
    return el;
  }

  // ── Delete flow ──────────────────────────────────────────────────────────
  // Wired via `dialog-component:confirm->…#confirmDelete` on the root element,
  // bubbling up from the nested Ui::DialogComponent confirmation.
  async confirmDelete() {
    if (!this.deleteUrlValue) return;

    let succeeded = false;
    try {
      const response = await fetch(this.deleteUrlValue, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken(),
          Accept: 'application/json'
        }
      });
      succeeded = response.ok && (await response.json()).success !== false;
    } catch {
      succeeded = false;
    }

    if (!succeeded) {
      this.showError('Could not delete the document. Please try again.');
      return;
    }

    this.renderEmpty();
    this.dispatch('deleted');
  }

  renderEmpty() {
    this.clearError();
    this.selectedFile = null;
    if (this.hasInputTarget) this.inputTarget.value = '';
    if (!this.hasDropzoneTarget) return;

    const dz = this.dropzoneTarget;
    dz.classList.remove(DROPZONE.filled);
    dz.setAttribute('aria-label', 'Upload document');

    const fileInfo = dz.querySelector(FILE_INFO.selector);
    if (!fileInfo) return;
    fileInfo.classList.remove(FILE_INFO.hasFile);

    const uploadText = document.createElement('div');
    uploadText.className = 'ui-document-uploader__upload-text';
    uploadText.append(
      this.buildSpan('ui-document-uploader__upload-link', this.titleValue),
      this.buildSpan('ui-document-uploader__upload-body', 'or drop it here')
    );

    const children = [uploadText];
    if (this.subtitleValue) {
      children.push(this.buildSpan('ui-document-uploader__subtitle', this.subtitleValue));
    }
    fileInfo.replaceChildren(...children);
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return (meta && meta.content) || '';
  }

  // ── Radio-group syncing ──────────────────────────────────────────────────
  // In selectable mode, the file input only takes part in the form submission
  // when this uploader's radio is the selected one. Picking a sibling section
  // strips the input's `name` so the binary isn't sent; re-selecting the
  // parent restores it. Keeps the input fully interactive (replace still works).
  handleRadioGroupChange(event) {
    if (!this.hasRadioTarget) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (target.name !== this.radioTarget.name) return;
    this.syncInputName();
  }

  syncInputName() {
    if (!this.hasInputTarget || !this.hasRadioTarget) return;

    if (this.originalInputName === undefined) {
      this.originalInputName = this.inputTarget.getAttribute('name') || '';
    }
    if (this.originalInputName === '') return;

    if (this.radioTarget.checked) {
      this.inputTarget.setAttribute('name', this.originalInputName);
    } else {
      this.inputTarget.removeAttribute('name');
    }
  }

  selectOwnRadio() {
    if (!this.hasRadioTarget || this.radioTarget.checked) return;
    this.radioTarget.checked = true;
    this.syncInputName();
  }

  // ── Drag visuals ─────────────────────────────────────────────────────────
  setDragging(active) {
    if (!this.hasDropzoneTarget) return;
    this.dropzoneTarget.classList.toggle(DROPZONE.dragging, active);
    if (this.hasDropOverlayTarget) this.dropOverlayTarget.hidden = !active;
  }

  // ── Error message banner ─────────────────────────────────────────────────
  showError(message) {
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.add(DROPZONE.error);
    if (!this.hasMessageTarget) return;

    this.messageTarget.removeAttribute('hidden');
    this.messageTarget.classList.add(HINT.error);
    this.messageTarget.setAttribute('role', 'alert');
    this.messageTarget.setAttribute('aria-live', 'assertive');
    this.setMessageText(message);
  }

  clearError() {
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove(DROPZONE.error);
    if (!this.hasMessageTarget) return;

    this.messageTarget.classList.remove(HINT.error);
    this.messageTarget.setAttribute('aria-live', 'polite');
    this.messageTarget.removeAttribute('role');

    const defaultHint = this.messageTarget.dataset.defaultHint || '';
    this.setMessageText(defaultHint);
    if (defaultHint) {
      this.messageTarget.removeAttribute('hidden');
    } else {
      this.messageTarget.setAttribute('hidden', '');
    }
  }

  // The message is a Ui::HintComponent; its text lives in the
  // `[data-hint-text]` span and the error icon/colour swap is CSS-driven
  // by the `ui-hint--error` modifier.
  setMessageText(text) {
    const textEl = this.messageTarget.querySelector('[data-hint-text]');
    if (textEl) textEl.textContent = text;
  }

  // ── State predicates / utilities ─────────────────────────────────────────
  isDisabled() {
    return this.element.getAttribute('aria-busy') === 'true' ||
      (this.hasInputTarget && this.inputTarget.disabled);
  }

  isFilled() {
    return this.hasDropzoneTarget && this.dropzoneTarget.classList.contains(DROPZONE.filled);
  }

  isSelectable() {
    return this.hasRadioTarget;
  }

  formatSize(bytes) {
    if (!bytes) return '';
    const thresh = 1024;
    if (bytes < thresh) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let unitIndex = -1;
    let value = bytes;
    do { value /= thresh; unitIndex += 1; } while (value >= thresh && unitIndex < units.length - 1);
    return `${value.toFixed(1)} ${units[unitIndex]}`;
  }
}
